import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

// Plain names for the backtest's feature keys (backend/app/backtesting/news_movers.py).
const LABELS: Record<string, string> = {
  contract: 'Contract / order',
  guidance: 'Raised guidance',
  demand: 'Demand / shortage',
  policy: 'Policy / government',
  geopolitical: 'Geopolitical',
  earnings_beat: 'Beat estimates',
  analyst_up: 'Analyst upgrade',
  product: 'Product / launch',
  takeover: 'Takeover talk',
  sympathy: 'Moving with peers',
  earnings_miss: 'Missed estimates',
  analyst_down: 'Analyst downgrade',
  financing: 'Offering / dilution',
  legal: 'Legal / probe',
  fda: 'FDA approval',
  trial: 'Positive trial data',
  acquired: 'Being acquired',
  profit_turn: 'First profit',
  squeeze: 'Short squeeze',
  ai: 'AI / data centre',
  index_add: 'Index inclusion',
  partnership: 'Partnership',
  record: 'Record results',
  buyback: 'Buyback',
  insider_buy: 'Insider buying',
  need_named: 'Named customer or size',
  up_reason: 'Rise with a stated reason',
  up_noreason: 'Rise, no reason given',
  down: 'Down headline',
  coverage_surge: 'Coverage surge',
  coverage_first: 'First news in 6 months',
};
const label = (k: string) => (k.startsWith('pub_') ? `From ${k.slice(4)}` : LABELS[k] || k);
const pct = (x?: number | null, digits = 1) => (x === null || x === undefined ? '—' : `${(x * 100).toFixed(digits)}%`);

const OUTCOME_ROWS: [string, string][] = [
  ['up20_1m', '+20% within 1 month'],
  ['up20_3m', '+20% within 3 months'],
  ['up50_3m', '+50% within 3 months'],
  ['up50_6m', '+50% within 6 months'],
  ['dbl_6m', 'Doubled within 6 months'],
];

const HORIZONS: { key: string; title: string }[] = [
  { key: 'short', title: 'Short range · 1 month' },
  { key: 'mid', title: 'Mid range · 3–6 months' },
  { key: 'double', title: 'Doubling odds · 6 months' },
];

const Row: React.FC<{ p: any }> = ({ p }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen(!open)} aria-label="show articles">
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{p.rank}</TableCell>
        <TableCell>
          <Link component="button" variant="body2" fontWeight={700} onClick={() => navigate(`/scoring?symbol=${p.symbol}`)}>
            {p.symbol}
          </Link>
          <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
            {p.name}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={700}>
            {pct(p.est)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            similar {pct(p.similar)}
          </Typography>
        </TableCell>
        <TableCell align="right" sx={{ color: 'error.main' }}>
          {pct(p.crash, 0)}
        </TableCell>
        <TableCell align="right">${p.close.toFixed(2)}</TableCell>
        <TableCell align="right" sx={{ color: p.r3m >= 0 ? 'success.main' : 'error.main' }}>
          {p.r3m >= 0 ? '+' : ''}
          {pct(p.r3m, 0)}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {p.features.map((f: string) => (
              <Chip key={f} label={label(f)} size="small" variant="outlined" />
            ))}
          </Box>
        </TableCell>
        <TableCell align="right">{p.articles_30d}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5 }}>
              {p.articles.map((a: any, i: number) => (
                <Box key={i} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    {a.url ? (
                      <Link href={a.url} target="_blank" rel="noopener noreferrer">
                        {a.title}
                      </Link>
                    ) : (
                      a.title
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.date} · {a.publisher}
                    {a.tags.length > 0 && ` · ${a.tags.map(label).join(', ')}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const NewsMovers: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [record, setRecord] = useState<any>(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [snap, tr] = await Promise.all([apiService.getNewsMovers(), apiService.getNewsMoversTrackRecord()]);
      setData(snap);
      setRecord(tr);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load News Movers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await apiService.refreshNewsMovers();
    } finally {
      setTimeout(() => {
        setRefreshing(false);
        load();
      }, 150000);
    }
  };

  const h = HORIZONS[tab].key;
  const block = data?.horizons?.[h];
  const tr = record?.[h];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            News Movers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>
            Stocks whose last 30 days of news match the kinds of news that came before rises in 2017–2026, tested
            against similar stocks without that news. No news source proved more reliable than another, so the
            source is shown but not scored.
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          {data && (
            <Typography variant="caption" color="text.secondary" display="block">
              News to {data.as_of} · {data.articles.toLocaleString()} articles · {data.companies_with_news.toLocaleString()} companies
            </Typography>
          )}
          <Tooltip title="Rebuild from the latest news (about 2 minutes)">
            <span>
              <Button size="small" startIcon={refreshing ? <CircularProgress size={14} /> : <Refresh />} onClick={refresh} disabled={refreshing}>
                {refreshing ? 'Rebuilding…' : 'Rebuild'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="info">{error}</Alert>}
      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {data && (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ mb: 2 }}>
            {HORIZONS.map((x) => (
              <Tab key={x.key} label={x.title} />
            ))}
          </Tabs>

          {block && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Listed: {block.min > 1 ? `at least ${block.min} of ` : 'any of '}
                  {block.features.map(label).join(' · ')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  How often stocks with this news went on to make a big move, 2017–2026, compared with similar stocks (same
                  month, same volatility band, same 3-month run):
                </Typography>
                <TableContainer>
                  <Table size="small" sx={{ maxWidth: 760 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Move</TableCell>
                        <TableCell align="right">This news</TableCell>
                        <TableCell align="right">Similar stocks</TableCell>
                        <TableCell align="right">All stocks</TableCell>
                        <TableCell align="right">2017–21 / 2022–26</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {OUTCOME_ROWS.map(([k, name]) => {
                        const r = block.stats?.[k];
                        if (!r?.all?.hit && r?.all?.hit !== 0) return null;
                        const bold = k === block.outcome;
                        const half = (x: any) => (x?.lift === undefined ? '—' : `${x.lift >= 0 ? '+' : ''}${(x.lift * 100).toFixed(1)}`);
                        return (
                          <TableRow key={k} sx={bold ? { '& td': { fontWeight: 700 } } : undefined}>
                            <TableCell>{name}</TableCell>
                            <TableCell align="right">{pct(r.all.hit)}</TableCell>
                            <TableCell align="right">{pct(r.all.matched_base)}</TableCell>
                            <TableCell align="right">{pct(r.all.universe)}</TableCell>
                            <TableCell align="right">
                              {half(r.first_half)} / {half(r.second_half)} pts
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Average return vs all stocks: 1 month {pct(block.excess?.['1m']?.excess)}, 3 months {pct(block.excess?.['3m']?.excess)}, 6
                  months {pct(block.excess?.['6m']?.excess)}. The edge is a few points: most listed stocks do not make the move, and
                  news did not beat chance on most measures once volatility was accounted for. These rules were chosen after
                  testing, so the figures are optimistic; the page's own frozen picks are the real test.
                  {tr && tr.matured > 0
                    ? ` So far: ${pct(tr.hit_rate)} of ${tr.matured} matured picks did it (average ${pct(tr.avg_return)}, S&P 500 ${pct(tr.avg_spy)}).`
                    : ` Record started ${record?.since || data.as_of}; picks are scored once their horizon has passed.`}
                </Typography>
              </CardContent>
            </Card>
          )}

          {block && block.picks.length === 0 && <Alert severity="info">No stock scores above zero on this horizon today.</Alert>}
          {block && block.picks.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>#</TableCell>
                    <TableCell>Stock</TableCell>
                    <TableCell align="right">
                      <Tooltip title={`Estimated chance to ${block.label}: what stocks like this one (volatility, 3-month run) did historically, plus this news' measured edge`}>
                        <span>Chance</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={`Historical chance that stocks like this one ${block.crash_label}`}>
                        <span>Fall risk</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">3 months</TableCell>
                    <TableCell>Why (news in the last 30 days)</TableCell>
                    <TableCell align="right">Articles</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {block.picks.map((p: any) => (
                    <Row key={p.symbol} p={p} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default NewsMovers;
